import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useRef
} from 'react';
import dropRight from 'lodash/dropRight';
import fromPairs from 'lodash/fromPairs';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import without from 'lodash/without';
import size from 'lodash/size';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import filter from 'lodash/filter';

import { UUID } from '../../../types';
import {
  DropzoneHelperFileIds,
  DropzoneHelperFiles,
  DropzoneHelperFile,
  DropzoneHelperRequiredProps,
  DropzoneHelperRemovedFileIds,
  initialDropzoneState,
  initialDropzoneStateIds
} from './DropzoneHelper.types';

import { usePreviousValue } from '../../../common/hooks/usePreviousValue';
import { useDownloadTypeFile } from '../../../main/s3Multipart/hooks/useDownloadTypeFile';

import {
  DropzoneTabs,
  DropzoneTabType,
  DropzoneTabOnChange
} from '../../dropzone/DropzoneTabs';
import {
  S3MultipartDropzoneArea,
  S3MultipartDropzoneAreaOnFilesAccepted,
  S3MultipartDropzoneAreaOnFileCreated,
  S3MultipartDropzoneAreaOnUploadProgress,
  S3MultipartDropzoneAreaOnFileUploaded,
  S3MultipartDropzoneAreaOnFileFailed
} from '../../dropzone/S3MultipartDropzoneArea';
import { DropzoneUploadedFiles } from '../../dropzone/DropzoneUploadedFiles';
import { dateFnsConvert } from '../../../utils/dateFnsConvert';

type DropzoneHelperProps = DropzoneHelperRequiredProps;

function DropzoneHelper({
  accept,
  customItem,
  disabled,
  initialFileIds = initialDropzoneStateIds,
  initialFiles = initialDropzoneState,
  maxFiles,
  maxSize,
  onChange,
  onChangeFiles,
  onDropRejected,
  onFilesAccepted,
  onFileCreated,
  onFileUploaded,
  onRemoveFile,
  preventMaxFilesOverload,
  resetStateOnInitialFilesChange,
  type,
  withFullscreenDropzone,
  withLd,
  withoutDownload,
  withoutDropzoneArea,
  withoutRemoveButton,
  withoutStatus,
  withoutTabs,
  syncUpdateAction = false
}: DropzoneHelperProps) {
  const removedFileIdsRef = useRef<DropzoneHelperRemovedFileIds>();
  const allFilesRef = useRef<DropzoneHelperFiles>({});
  const pendingProgressRef = useRef<{
    [id: string]: { [partNumber: number]: number };
  }>({});
  const progressFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const activeTabRef = useRef<DropzoneTabType>('general');

  const [activeTab, setActiveTab] = useState<DropzoneTabType>('general');

  const [dataParams, setDataParams] = useState<{
    [key: string]: boolean | string;
  } | null>(null);

  const [allFiles, setAllFiles] = useState<DropzoneHelperFiles>(initialFiles);

  const [allFileIds, setAllFileIds] =
    useState<DropzoneHelperFileIds>(initialFileIds);

  const [removedFileIds, setRemovedFileIds] =
    useState<DropzoneHelperRemovedFileIds>([]);

  const prevAllFileIds = usePreviousValue(allFileIds);

  const prevAllFiles = usePreviousValue(allFiles);

  removedFileIdsRef.current = removedFileIds;
  allFilesRef.current = allFiles;
  activeTabRef.current = activeTab;

  const handleTabChange = useCallback<DropzoneTabOnChange>(
    (tabName) => {
      setActiveTab(tabName);

      if (tabName === 'ld') {
        setDataParams({
          ldFileAt: dateFnsConvert.toDateTimeWithTimezone(new Date().toString())
        });
        return;
      }

      setDataParams({ [tabName]: true });
    },
    [setDataParams]
  );

  const handleFilesDropped =
    useCallback<S3MultipartDropzoneAreaOnFilesAccepted>(
      (files) => {
        onFilesAccepted?.();
        setAllFiles((prevState) => ({
          ...prevState,
          ...fromPairs<DropzoneHelperFile>(
            files
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  numeric: true,
                  sensitivity: 'base'
                })
              )
              .map((file) => [
                file.id,
                {
                  ...file,
                  state: 'processing' as const,
                  activeTab,
                  progresses: {}
                }
              ])
          )
        }));
      },
      [activeTab, onFilesAccepted]
    );

  const handleFileCreated = useCallback<S3MultipartDropzoneAreaOnFileCreated>(
    (file, presignedUrls) => {
      onFileCreated?.(file.id);

      setAllFiles((prevState) => ({
        ...prevState,
        [file.id]: {
          ...prevState[file.id],
          progresses: fromPairs(
            (size(presignedUrls) > 1
              ? dropRight(presignedUrls)
              : presignedUrls
            ).map((_, index) => [index, 0])
          )
        }
      }));
    },
    [onFileCreated]
  );

  const handleUploadProgress =
    useCallback<S3MultipartDropzoneAreaOnUploadProgress>(
      (id, partNumber, progress) => {
        if (size(removedFileIdsRef.current) > 0) {
          const isRemovedFileId = find(
            removedFileIdsRef.current,
            (removedFileId) => removedFileId === id
          );
          if (isRemovedFileId) {
            return;
          }
        }

        if (!pendingProgressRef.current[id]) {
          pendingProgressRef.current[id] = {};
        }
        pendingProgressRef.current[id][partNumber] = progress;

        if (!progressFlushTimerRef.current) {
          progressFlushTimerRef.current = setTimeout(() => {
            progressFlushTimerRef.current = null;
            const updates = pendingProgressRef.current;
            pendingProgressRef.current = {};
            const currentActiveTab = activeTabRef.current;
            setAllFiles((prevState) => {
              const newState = { ...prevState };
              Object.keys(updates).forEach((fileId) => {
                newState[fileId] = {
                  ...newState[fileId],
                  progresses: {
                    ...(newState[fileId]?.progresses || {}),
                    ...updates[fileId]
                  },
                  activeTab: currentActiveTab
                };
              });
              return newState;
            });
          }, 100);
        }
      },
      []
    );

  const handleFileUploaded = useCallback<S3MultipartDropzoneAreaOnFileUploaded>(
    (id, uploadedId, uploadedFile, file) => {
      onFileUploaded?.(id, uploadedId, uploadedFile, file);

      setAllFiles((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          progresses: fromPairs(
            keys(prevState[id]?.progresses || {}).map((p) => [p, 100])
          ),
          state: 'finished' as const,
          uploadedId,
          activeTab,
          ...(file?.dpi ? { dpi: file.dpi } : {})
        }
      }));
      setAllFileIds((prevState) => [...prevState, uploadedId]);
    },
    [activeTab, onFileUploaded]
  );

  const handleFileFailed = useCallback<S3MultipartDropzoneAreaOnFileFailed>(
    (id) =>
      setAllFiles((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          state: 'failed' as const,
          activeTab
        }
      })),
    [activeTab]
  );

  const handleRemoveItem = useCallback<(id: string) => void>(
    (id: string) => {
      setAllFiles((prevState) => omit<DropzoneHelperFiles>(prevState, id));
      setAllFileIds((prevState) =>
        without<string>(prevState, allFilesRef.current[id]?.uploadedId || id)
      );
      setRemovedFileIds((prevState) => [...prevState, id]);
      onRemoveFile?.(allFilesRef.current[id]?.uploadedId || id);
    },
    [onRemoveFile]
  );

  const { onDownload } = useDownloadTypeFile({ type });

  const handleDownloadItem = useCallback<(id: string) => void>(
    (id: string) =>
      onDownload((allFilesRef.current[id]?.uploadedId || id) as UUID),
    [onDownload]
  );

  useEffect(() => {
    return () => {
      if (progressFlushTimerRef.current) {
        clearTimeout(progressFlushTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (size(removedFileIds) > 0) {
      removedFileIds.forEach((removedFileId) => {
        if (allFiles[removedFileId]) {
          setAllFiles((prevState) =>
            omit<DropzoneHelperFiles>(prevState, removedFileId)
          );
          setAllFileIds((prevState) =>
            without<string>(prevState, allFiles[removedFileId].uploadedId)
          );
          setRemovedFileIds((prevState) =>
            without<string>(prevState, removedFileId)
          );
        }
      });
    }
  }, [allFileIds, allFiles, removedFileIds]);

  useEffect(() => {
    if (!isEqual(prevAllFileIds, allFileIds)) {
      onChange?.(allFileIds);
    }
  }, [allFileIds, onChange, prevAllFileIds]);

  useEffect(() => {
    if (!isEqual(prevAllFiles, allFiles)) {
      onChangeFiles?.(allFiles);
    }
  }, [allFiles, prevAllFiles, onChangeFiles]);

  useEffect(() => {
    if (resetStateOnInitialFilesChange) {
      setAllFiles(initialFiles);
      setAllFileIds(initialFileIds);
    }
  }, [initialFiles, initialFileIds, resetStateOnInitialFilesChange]);

  return (
    <Fragment>
      {withoutTabs ? null : (
        <DropzoneTabs
          allFiles={allFiles}
          activeTab={activeTab}
          onChange={handleTabChange}
          withLd={withLd}
        />
      )}
      {withoutDropzoneArea ? null : (
        <S3MultipartDropzoneArea
          accept={accept}
          activeFilesCount={size(
            filter(
              allFiles,
              (file) =>
                file?.state === 'initialized' ||
                file?.state === 'processing' ||
                file?.state === 'finished'
            )
          )}
          dataParams={dataParams}
          disabled={disabled}
          maxFiles={maxFiles}
          maxSize={maxSize}
          onDropRejected={onDropRejected}
          onFileCreated={handleFileCreated}
          onFileFailed={handleFileFailed}
          onFilesAccepted={handleFilesDropped}
          onFileUploaded={handleFileUploaded}
          onUploadProgress={handleUploadProgress}
          preventMaxFilesOverload={preventMaxFilesOverload}
          syncUpdateAction={syncUpdateAction}
          type={type}
          withFullscreenDropzone={withFullscreenDropzone}
        />
      )}
      <DropzoneUploadedFiles
        activeTab={activeTab}
        allFiles={allFiles}
        onRemoveFile={handleRemoveItem}
        onDownloadFile={handleDownloadItem}
        withoutDownload={withoutDownload}
        withoutRemoveButton={withoutRemoveButton}
        withoutStatus={withoutStatus}
        customItem={customItem}
        showOriginalPreview={type === 'imageCrops'}
      />
    </Fragment>
  );
}

export default DropzoneHelper;
